export interface ProviderFamily {
  id: string;
  label: string;
  methodLabel: string;
}

export type ProviderCredentialIdentifier = "email" | "username";

export interface ProviderCatalogEntry {
  label: string;
  family?: ProviderFamily;
  logo?: { id?: string; type: "png" | "svg" };
  brandColor?: string;
  credentialIdentifier?: ProviderCredentialIdentifier;
  credentialInstructions?: string;
}

const garminConnect: ProviderFamily = {
  id: "garmin",
  label: "Garmin",
  methodLabel: "Garmin Connect",
};

const garminExport: ProviderFamily = {
  id: "garmin",
  label: "Garmin",
  methodLabel: "Data export",
};

const whoopCloud: ProviderFamily = { id: "whoop", label: "WHOOP", methodLabel: "Cloud" };
const whoopBluetooth: ProviderFamily = {
  id: "whoop",
  label: "WHOOP",
  methodLabel: "Bluetooth",
};

const zeppCloud: ProviderFamily = {
  id: "zepp",
  label: "Zepp",
  methodLabel: "Zepp cloud",
};

const zeppApp: ProviderFamily = {
  id: "zepp",
  label: "Zepp",
  methodLabel: "Zepp app (Zepp OS)",
};

const kayaWeb: ProviderFamily = { id: "kaya", label: "Kaya", methodLabel: "Web" };
const kayaExport: ProviderFamily = {
  id: "kaya",
  label: "Kaya",
  methodLabel: "Data export (CSV file)",
};

const auroraBoardCredentials = {
  credentialIdentifier: "username" as const,
  credentialInstructions:
    "Use an existing board account after accepting its terms of use and privacy policy in the board app.",
};

/**
 * Canonical user-facing presentation for technical provider IDs.
 *
 * Provider IDs remain the ingestion and provenance identifiers. The optional
 * family describes only how multiple connection methods are presented together
 * in client provider settings.
 */
export const PROVIDER_CATALOG: Readonly<Record<string, ProviderCatalogEntry>> = {
  kilter: { label: "Kilter Board" },
  tension: { label: "Tension Board", ...auroraBoardCredentials },
  grasshopper: { label: "Grasshopper Board", ...auroraBoardCredentials },
  decoy: { label: "Decoy Board", ...auroraBoardCredentials },
  soill: { label: "So iLL Board", ...auroraBoardCredentials },
  touchstone: { label: "Touchstone Board", ...auroraBoardCredentials },
  "aurora-board": { label: "Aurora Board", ...auroraBoardCredentials },
  crimpd: { label: "Crimpd" },
  "eight-a": { label: "8a.nu / Vertical-Life" },
  moonboard: { label: "MoonBoard" },
  google: { label: "Google", logo: { type: "svg" } },
  apple: { label: "Apple", logo: { type: "svg" } },
  github: { label: "GitHub" },
  slack: { label: "Slack" },
  strava: { label: "Strava", logo: { type: "svg" } },
  wahoo: { label: "Wahoo", logo: { type: "png" } },
  fitbit: { label: "Fitbit", logo: { type: "svg" } },
  "ride-with-gps": { label: "Ride with GPS", logo: { type: "png" } },
  "intervals.icu": { label: "Intervals.icu" },
  withings: { label: "Withings", logo: { type: "png" } },
  garmin: { label: "Garmin", family: garminConnect, logo: { type: "svg" } },
  "garmin-dump": {
    label: "Garmin Dump",
    family: garminExport,
    logo: { id: "garmin", type: "svg" },
  },
  "fit-file": { label: "FIT File" },
  polar: { label: "Polar", logo: { type: "png" } },
  whoop: { label: "WHOOP (Cloud)", family: whoopCloud, logo: { type: "png" } },
  peloton: { label: "Peloton", logo: { type: "svg" } },
  oura: { label: "Oura", logo: { type: "png" } },
  zwift: { label: "Zwift", logo: { type: "png" } },
  suunto: { label: "Suunto", logo: { type: "png" } },
  trainerroad: { label: "TrainerRoad", logo: { type: "svg" } },
  komoot: { label: "Komoot", logo: { type: "svg" } },
  concept2: { label: "Concept2", logo: { type: "png" } },
  coros: { label: "COROS", logo: { type: "png" } },
  "cycling-analytics": { label: "Cycling Analytics" },
  cycling_analytics: { label: "Cycling Analytics", logo: { type: "png" } },
  decathlon: { label: "Decathlon", logo: { type: "png" } },
  "eight-sleep": { label: "Eight Sleep", logo: { type: "svg" } },
  fatsecret: { label: "fatsecret", logo: { type: "png" } },
  ziva: { label: "Ziva" },
  mapmyfitness: { label: "MapMyFitness", logo: { type: "png" } },
  ultrahuman: { label: "Ultrahuman", logo: { type: "png" } },
  "amazfit-zepp": { label: "Amazfit/Zepp", family: zeppCloud, logo: { id: "zepp", type: "png" } },
  "zos-app": { label: "Zepp OS App", family: zeppApp, logo: { id: "zepp", type: "png" } },
  "mountain-project": { label: "Mountain Project", brandColor: "#1F5A88" },
  openbeta: { label: "OpenBeta" },
  thecrag: { label: "theCrag" },
  wger: { label: "Wger", logo: { type: "png" } },
  xert: { label: "Xert", logo: { type: "png" } },
  "apple-health": { label: "Apple Health" },
  apple_health: { label: "Apple Health", logo: { type: "png" } },
  "apple-watch": { label: "Apple Watch", logo: { type: "svg" } },
  "strong-csv": { label: "Strong", logo: { type: "png" } },
  "hang-ten": { label: "Hang Ten", logo: { type: "png" } },
  "cronometer-csv": { label: "Cronometer", logo: { type: "png" } },
  kaya: { label: "Kaya", family: kayaWeb, logo: { type: "png" } },
  "kaya-export": { label: "Kaya", family: kayaExport, logo: { id: "kaya", type: "png" } },
  bodyspec: { label: "BodySpec", logo: { type: "png" } },
  dofek: { label: "Dofek", brandColor: "#4A9D8E" },
  manual_review: { label: "Manual review" },
  whoop_ble: {
    label: "WHOOP (Bluetooth)",
    family: whoopBluetooth,
    logo: { id: "whoop", type: "png" },
  },
  ble_heart_rate: { label: "Heart Rate Monitor (Bluetooth)", brandColor: "#E0245E" },
};

export function providerCatalogEntry(providerId: string): ProviderCatalogEntry | undefined {
  return PROVIDER_CATALOG[providerId];
}

export function providerFamily(providerId: string): ProviderFamily | null {
  return providerCatalogEntry(providerId)?.family ?? null;
}

export type ProviderEntryGroup<T extends { id: string }> =
  | { kind: "provider"; provider: T }
  | {
      kind: "family";
      family: Pick<ProviderFamily, "id" | "label">;
      providers: readonly [T, T, ...T[]];
    };

function hasAtLeastTwo<T>(entries: readonly T[]): entries is readonly [T, T, ...T[]] {
  return entries.length >= 2;
}

/**
 * Groups only the provider IDs explicitly assigned to the same family,
 * then orders the cards alphabetically by their displayed labels.
 * A family with one available connection method remains a regular provider
 * entry, so the clients do not show a meaningless method chooser.
 */
export function groupProviderEntries<T extends { id: string; label?: string }>(
  providers: readonly T[],
): ProviderEntryGroup<T>[] {
  const membersByFamily = new Map<string, T[]>();
  for (const provider of providers) {
    const family = providerFamily(provider.id);
    if (!family) continue;
    const members = membersByFamily.get(family.id) ?? [];
    members.push(provider);
    membersByFamily.set(family.id, members);
  }

  const emittedFamilies = new Set<string>();
  const groups: ProviderEntryGroup<T>[] = [];
  for (const provider of providers) {
    const family = providerFamily(provider.id);
    const members = family ? membersByFamily.get(family.id) : undefined;
    if (!family || !members || !hasAtLeastTwo(members)) {
      groups.push({ kind: "provider", provider });
      continue;
    }
    if (emittedFamilies.has(family.id)) continue;
    emittedFamilies.add(family.id);
    groups.push({
      kind: "family",
      family: { id: family.id, label: family.label },
      providers: members,
    });
  }
  const label = (group: ProviderEntryGroup<T>) =>
    group.kind === "family"
      ? group.family.label
      : (group.provider.label ??
        providerCatalogEntry(group.provider.id)?.label ??
        group.provider.id);
  return groups.sort((a, b) => label(a).localeCompare(label(b), "en", { sensitivity: "base" }));
}
